import { Injectable } from '@angular/core';

import { ApiHelperService } from '../../core/api-helper.service';
import { Organization } from '../organization/organization.model';
import { UserMarkup, UserPreview } from '../user/user.model';
import { User } from './user.model';
import { environment } from '../../../environments/environment';

interface InviteData {
    firstName: string;
    lastName: string;
    termsAccepted: boolean;
    isFreeTrialUser: boolean;
};

interface InviteResponse {
    user: Partial<User>;
    organization: Partial<Organization>;
}

@Injectable({
    providedIn: 'root'
})
export class UserService {

    options = { authenticate: true, setRoot: true };

    constructor( private apiHelper: ApiHelperService) {}

    bootstrapSession(): Promise<User> {
        return this.apiHelper.apiCallSane(
            '/auth/bootstrap',
            'POST',
            this.apiHelper.getDefaultHeaders(),
            null,
            { },
            this.options
        ).then((pu: Partial<User>) => new User(pu));
    }

    signOutSession(): Promise<void> {
        return this.apiHelper.apiCallSane(
            '/auth/sign-out',
            'POST',
            this.apiHelper.getDefaultHeaders(),
            null,
            null,
            { authenticate: false, setRoot: true }
        );
    }

    createNewUser(user: Partial<User>,organization: Partial<Organization>): Promise<any> {
        return this.apiHelper.apiCallSane(
            '/auth/testing',
            'POST',
            this.apiHelper.getDefaultHeaders(),
            null,
            { user, organization },
            this.options
        ).then((data: any) => data);
    }

    createUser(user: Partial<User>, password: string): Promise<User> {
        return this.apiHelper.apiCallSane(
            '/user',
            'PUT',
            this.apiHelper.getDefaultHeaders(),
            null,
            { user, password },
            this.options
        ).then((pu: Partial<User>) => new User(pu));
    }

    patchUser(id:string, user: Partial<User>): Promise<User> {
        return this.apiHelper.apiCallSane(
            '/user',
            'PATCH',
            this.apiHelper.getDefaultHeaders(),
            { id },
            user,
            this.options
        ).then((pu: Partial<User>) => new User(pu));
    }


    completeInvite(id: string, data: InviteData): Promise<{ user: User, organization: Organization }> {
        return this.apiHelper.apiCallSane(
            '/agg/complete-invite',
            'PUT',
            this.apiHelper.getDefaultHeaders(),
            { id },
            data,
            this.options
        ).then(({ user, organization }: InviteResponse) =>
            ({
                user: new User(user),
                organization: new Organization(organization)
            }));
    }

    checkEmail(email: string): Promise<boolean> {
        return this.apiHelper.apiCallSane(
            '/anon/user/check',
            'GET',
            this.apiHelper.getDefaultHeaders(),
            { email },
            null,
            this.options
        );
    }

    inviteUser(user: Partial<User>, invitedBy: string): Promise<User> {
        return this.apiHelper.apiCallSane(
            '/user/invite',
            'PUT',
            this.apiHelper.getDefaultHeaders(),
            null,
            { user, invitedBy },
            this.options
        ).then((pu: Partial<User>) => new User(pu));
    }

    inviteTrialUser(user: Partial<User>, invitedBy: string): Promise<User> {
        user.isFreeTrialAccount = true;
        return this.apiHelper.apiCallSane(
            '/user/invite',
            'PUT',
            this.apiHelper.getDefaultHeaders(),
            null,
            { user, invitedBy },
            this.options
        ).then((pu: Partial<User>) => new User(pu));
    }

    updateUserMarkup(id: string, data: UserMarkup) {
        return this.apiHelper.apiCallSane(
            '/user',
            'PATCH',
            this.apiHelper.getDefaultHeaders(),
            { defaultUserMarkup: id },
            data,
            this.options
        );
    }

    getUserById(id: string): Promise<User> {
        return this.apiHelper.apiCallSane(
            '/user',
            'GET',
            this.apiHelper.getDefaultHeaders(),
            { id },
            null,
            this.options
        ).then((up: Partial<User>) => new User(up));
    }

    getUserByCompatId(compatId: string): Promise<User> {
        return this.apiHelper.apiCallSane(
            '/user',
            'GET',
            this.apiHelper.getDefaultHeaders(),
            { compatId },
            null,
            this.options
        ).then((up: Partial<User>) => new User(up));
    }


    getUserPreviews(): Promise<UserPreview[]> {
        return this.apiHelper.apiCallSane(
            '/users/preview',
            'GET',
            this.apiHelper.getDefaultHeaders(),
            null,
            null,
            this.options
        ).then((users: Partial<UserPreview>[]) => users.map(user => new UserPreview(user)));
    }

    getOrganizationUsers(organizationId: string): Promise<UserPreview[]> {
        return this.apiHelper.apiCallSane(
            '/users/preview',
            'GET',
            this.apiHelper.getDefaultHeaders(),
            { organizationId },
            null,
            this.options
        ).then((users: Partial<UserPreview>[]) => users.map(user => new UserPreview(user)));
    }

    getGroupUsers(groupId: string) {
        return this.apiHelper.apiCallSane(
            '/users/preview',
            'GET',
            this.apiHelper.getDefaultHeaders(),
            { groupId },
            null,
            this.options
        ).then((users: Partial<UserPreview>[]) => users.map(user => new UserPreview(user)));
    }

    getTeamUsers(teamId: string) {
        return this.apiHelper.apiCallSane(
            '/users/preview',
            'GET',
            this.apiHelper.getDefaultHeaders(),
            { teamId },
            null,
            this.options
        ).then((users: Partial<UserPreview>[]) => users.map(user => new UserPreview(user)));
    }
}
